import { useState, useEffect } from 'react'
import { Copy, Check, Eye, Code, Settings, Mail, ExternalLink, HelpCircle, Loader2, RefreshCw, ChevronDown, ChevronUp, RotateCcw, Shield, Save } from 'lucide-react'
import { PageHeader } from '@/components/dashboard-ui'
import WidgetPreview from '../components/WidgetPreview'
import WidgetAnimationsSection, { type WidgetAnimationsConfig } from '../components/widget/WidgetAnimationsSection'
import api from '../lib/api'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '../hooks/useAuth'
import { useBrand } from '@/hooks/useBrand'

export default function WidgetPage() {
  const { agent } = useAuth()
  const brand = useBrand()
  const isPlatformAdmin = (agent as any)?.isPlatformAdmin === true
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<'preview' | 'embed' | 'settings'>('preview')
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [developerEmail, setDeveloperEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [isRegeneratingKey, setIsRegeneratingKey] = useState(false)
  const [generatedPrompt, setGeneratedPrompt] = useState<string | null>(null)
  const [orgName, setOrgName] = useState('')
  const [orgWebsite, setOrgWebsite] = useState('')
  const [showPromptPreview, setShowPromptPreview] = useState(false)
  const { toast } = useToast()
  const [config, setConfig] = useState({
    clientId: '',
    widgetKey: '',
    position: 'bottom-right',
    theme: 'light',
    primaryColor: '#C73E2D',
    autoOpen: false,
    showOnMobile: true,
    proactiveEnabled: false,
    proactiveDelay: 15,
    proactiveMessage: '',
    contactEmail: '',
    bookingUrl: '',
    // New fields
    companyName: '',
    logoUrl: '',
    assistantName: '',
    welcomeMessage: '',
    attentionGrabber: true,
    conversationStarters: [] as Array<{ question: string; answer: string; label: string }>,
    // Browser voice + animations
    webVoiceEnabled: false,
    widgetAnimations: null as WidgetAnimationsConfig | null,
  })

  // Load organization config on mount
  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      setIsLoading(true)
      // Get current user's organization
      const orgResponse = await api.get('/api/organizations/current')
      if (orgResponse.data.success && orgResponse.data.organization) {
        const org = orgResponse.data.organization
        setOrgName(org.name || '')
        const detectedWebsite = org.website || org.settings?.website || org.settings?.scrapedWebsite || org.settings?.voiceConfig?.website || ''
        setOrgWebsite(detectedWebsite)

        // Fallback: try scraped data if no website in org record
        if (!detectedWebsite) {
          try {
            const scrapedRes = await api.get('/api/onboarding/scraped-data')
            if (scrapedRes.data?.data?.baseUrl) {
              setOrgWebsite(scrapedRes.data.data.baseUrl)
            }
          } catch {
            // Non-fatal
          }
        }
        const branding = org.branding || {}
        const settings = org.settings || {}

        setConfig({
          clientId: org.id,
          widgetKey: org.widgetKey || '',
          position: settings.position || 'bottom-right',
          theme: branding.theme || 'light',
          primaryColor: branding.primaryColor || '#C73E2D',
          autoOpen: settings.autoOpen || false,
          showOnMobile: settings.showOnMobile !== false,
          proactiveEnabled: settings.proactiveEnabled || false,
          proactiveDelay: settings.proactiveDelay || 15,
          proactiveMessage: settings.proactiveMessage || '',
          contactEmail: settings.contactEmail || '',
          bookingUrl: settings.bookingUrl || '',
          companyName: branding.companyName || '',
          logoUrl: branding.logoUrl || '',
          assistantName: settings.assistantName || '',
          welcomeMessage: settings.welcomeMessage || '',
          attentionGrabber: settings.attentionGrabber !== false,
          // Normalize legacy string[] into the new
          // {question, answer, label}[] shape on read so the UI works
          // against a single shape regardless of how old the persisted
          // settings are.
          conversationStarters: (() => {
            const raw = settings.conversationStarters
            const defaultStarters = [
              'What are your practice areas?',
              'How do I schedule a consultation?',
              'Do you offer free consultations?',
              'What should I bring to my first meeting?',
            ]
            const source = Array.isArray(raw) && raw.length > 0 ? raw : defaultStarters
            return source.map((s: string | { question?: string; answer?: string; label?: string }) =>
              typeof s === 'string'
                ? { question: s, answer: '', label: '' }
                : {
                    question: s.question || '',
                    answer: s.answer || '',
                    label: s.label || '',
                  }
            ).filter((s: { question: string }) => s.question.trim().length > 0)
          })(),
          webVoiceEnabled: settings.webVoiceEnabled === true,
          widgetAnimations: settings.widgetAnimations || null,
        })
      }
    } catch (error) {
      console.error('Failed to load widget config:', error)
      // Use defaults if loading fails
    } finally {
      setIsLoading(false)
    }
  }

  const saveConfig = async () => {
    try {
      setIsSaving(true)
      await api.put('/api/organizations/widget-settings', {
        position: config.position,
        theme: config.theme,
        primaryColor: config.primaryColor,
        autoOpen: config.autoOpen,
        showOnMobile: config.showOnMobile,
        proactiveEnabled: config.proactiveEnabled,
        proactiveDelay: config.proactiveDelay,
        proactiveMessage: config.proactiveMessage,
        contactEmail: config.contactEmail,
        bookingUrl: config.bookingUrl,
        companyName: config.companyName,
        logoUrl: config.logoUrl,
        assistantName: config.assistantName,
        welcomeMessage: config.welcomeMessage,
        attentionGrabber: config.attentionGrabber,
        // Drop entries with no question text. Trim all fields. Send the
        // structured shape so the API persists question + optional
        // answer + optional label.
        conversationStarters: config.conversationStarters
          .map((s) => ({
            question: s.question.trim(),
            answer: s.answer.trim(),
            label: s.label.trim(),
          }))
          .filter((s) => s.question.length > 0),
        webVoiceEnabled: config.webVoiceEnabled,
        widgetAnimations: config.widgetAnimations
      })
      toast({
        title: 'Settings saved',
        description: 'Your widget configuration has been updated.',
      })
    } catch (error) {
      console.error('Failed to save widget config:', error)
      toast({
        title: 'Error saving settings',
        description: 'Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const [showRegenConfirm, setShowRegenConfirm] = useState(false)

  const regeneratePrompt = async () => {
    try {
      setIsRegenerating(true)
      setShowRegenConfirm(false)
      const orgResponse = await api.get('/api/organizations/current')
      const organizationId = orgResponse.data.organization?.id
      if (!organizationId) throw new Error('Organization not found')

      const response = await api.post('/api/chat/system-prompt/regenerate', { organizationId })
      setGeneratedPrompt(response.data.prompt)
      setShowPromptPreview(true)
      toast({
        title: 'AI prompt regenerated',
        description: 'Your chat assistant\'s knowledge has been updated from your website and knowledge base.',
      })
    } catch (error) {
      toast({
        title: 'Regeneration failed',
        description: 'Please try again. Make sure your website has been scraped during onboarding.',
        variant: 'destructive'
      })
    } finally {
      setIsRegenerating(false)
    }
  }

  const regenerateWidgetKey = async () => {
    if (!confirm('Are you sure? Existing widget installations will stop working until you update them with the new key.')) return
    try {
      setIsRegeneratingKey(true)
      const response = await api.post('/api/organizations/regenerate-widget-key')
      if (response.data.success && response.data.widgetKey) {
        setConfig(prev => ({ ...prev, widgetKey: response.data.widgetKey }))
        toast({
          title: 'Widget key regenerated',
          description: 'Update your website embed code with the new key.',
        })
      }
    } catch (error) {
      console.error('Failed to regenerate widget key:', error)
      toast({
        title: 'Failed to regenerate key',
        description: 'Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsRegeneratingKey(false)
    }
  }

  // Derive API URL: prefer explicit env var, then same-host on known deployments
  const apiUrl = import.meta.env.VITE_API_URL ||
    (window.location.hostname.includes('railway.app')
      ? window.location.origin.replace(/dashboard/, 'api')
      : 'http://localhost:3000')
  const widgetUrl = apiUrl // Widget is served from API server at /widget/

  const generateEmbedCode = () => {
    // Simplified embed code — all config is fetched from the server
    const key = config.widgetKey || config.clientId
    return `<!-- ${brand.appName} Chat Widget -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${widgetUrl}/widget/intake-chat.iife.js';
    script.onload = function() {
      var Chat = window.IntakeChat && window.IntakeChat.IntakeChat
        ? window.IntakeChat.IntakeChat
        : window.IntakeChat;
      if (Chat) new Chat({ widgetKey: '${key}' });
    };
    document.head.appendChild(script);
  })();
</script>`
  }

  const isClientReady = Boolean(config.clientId)

  const copyToClipboard = () => {
    if (!isClientReady) return
    navigator.clipboard.writeText(generateEmbedCode())
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleEmailToDeveloper = () => {
    const subject = encodeURIComponent(`${brand.appName} Chat Widget Installation Code`)
    const body = encodeURIComponent(`Hi,

Please install the ${brand.appName} chat widget on our website by adding the following code just before the closing </body> tag:

${generateEmbedCode()}

INSTALLATION INSTRUCTIONS:
1. Copy all the code above
2. Paste it into your website's HTML, just before the closing </body> tag
3. Save and publish your changes
4. The chat widget should appear in the ${config.position} corner on your website

If you need any help, visit our documentation or contact support.

Thank you!`)

    window.location.href = `mailto:${developerEmail}?subject=${subject}&body=${body}`
    setEmailSent(true)
    setTimeout(() => {
      setShowEmailModal(false)
      setEmailSent(false)
      setDeveloperEmail('')
    }, 2000)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-4 w-96 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
          <div className="h-[400px] bg-muted animate-pulse rounded-xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <PageHeader
        eyebrow="Embed"
        title="Chat Widget"
        subtitle="Preview, customize, and install your website chat widget."
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <Code className="h-5 w-5 text-brand-royal" />
          </div>
        }
      />

      {/* Tab Navigation */}
      <div className="border-b border-border mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('preview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'preview'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
            }`}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={() => setActiveTab('embed')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'embed'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
            }`}
          >
            <Code className="w-4 h-4" />
            Install Widget
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
            }`}
          >
            <Settings className="w-4 h-4" />
            Configuration
          </button>
        </nav>
      </div>

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="rounded-xl border bg-card card-inset overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Live Widget Preview</h2>
            <p className="text-sm text-muted-foreground mt-1">
              This shows exactly how the widget will appear on your website
            </p>
          </div>
          <div className="p-4">
            <WidgetPreview config={config} organizationName={orgName} websiteUrl={orgWebsite} />
            <div className="mt-4 rounded-xl border border-brand-periwinkle/20 bg-brand-periwinkle/5 p-4">
              <p className="text-sm text-foreground/80">
                <strong className="text-brand-royal dark:text-brand-periwinkle">Tip:</strong> The widget will appear in the {config.position} corner.
                You can change the position and other settings in the Configuration tab.
                Try interacting with the widget — it's fully functional.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Embed Code Tab */}
      {activeTab === 'embed' && (
        <div className="space-y-6">
          {/* Loading notice when org ID isn't available yet */}
          {!isClientReady && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-amber-600 dark:text-amber-400 animate-spin flex-shrink-0" />
              <p className="text-sm text-foreground/80">
                Loading your organization details. The embed code will be available shortly.
              </p>
            </div>
          )}

          {/* Quick Actions */}
          <div className={`relative rounded-2xl border bg-card shadow-sm card-inset overflow-hidden ${!isClientReady ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="pointer-events-none absolute -top-20 -right-16 h-48 w-48 rounded-full bg-gradient-to-br from-brand-periwinkle/25 via-brand-royal/15 to-transparent blur-3xl" aria-hidden="true" />
            <div className="relative p-6">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">Install</p>
              <h2 className="text-xl font-heading font-semibold tracking-tight text-foreground mb-2">Install on Your Website</h2>
              <p className="text-sm text-muted-foreground mb-5">Choose the easiest option for you:</p>

              <div className="grid md:grid-cols-2 gap-3">
                <button
                  onClick={() => setShowEmailModal(true)}
                  disabled={!isClientReady}
                  className="flex items-center gap-3 bg-background p-4 rounded-xl border hover:border-brand-periwinkle/60 hover:shadow-sm transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="h-10 w-10 rounded-lg bg-brand-periwinkle/15 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-brand-royal dark:text-brand-periwinkle" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-foreground text-sm">Email to My Web Developer</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Send installation code with instructions</p>
                  </div>
                </button>

                <button
                  onClick={copyToClipboard}
                  disabled={!isClientReady}
                  className="flex items-center gap-3 bg-background p-4 rounded-xl border hover:border-brand-periwinkle/60 hover:shadow-sm transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5 text-muted-foreground" />}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-foreground text-sm">{copied ? 'Copied!' : 'Copy Code'}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Copy and paste it yourself</p>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Platform-Specific Guides */}
          <div className="rounded-xl border bg-card card-inset overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-muted-foreground" />
                Platform Installation Guides
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Step-by-step instructions for popular website platforms
              </p>
            </div>
            <div className="p-4 grid md:grid-cols-3 gap-4">
              {/* WordPress */}
              <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">W</div>
                  <h3 className="font-semibold text-foreground">WordPress</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  <strong>Option 1:</strong> Use a header/footer plugin (recommended)
                </p>
                <ol className="text-sm text-muted-foreground space-y-2 mb-4">
                  <li>1. Install "Insert Headers and Footers" plugin</li>
                  <li>2. Go to Settings &rarr; Insert Headers and Footers</li>
                  <li>3. Paste the code in the "Footer" section</li>
                  <li>4. Save changes</li>
                </ol>
                <p className="text-sm text-muted-foreground mb-3">
                  <strong>Option 2:</strong> Edit theme directly
                </p>
                <ol className="text-sm text-muted-foreground space-y-2 mb-4">
                  <li>1. Go to Appearance &rarr; Theme Editor</li>
                  <li>2. Open footer.php</li>
                  <li>3. Paste before <code className="bg-muted px-1 rounded">&lt;/body&gt;</code></li>
                </ol>
                <a
                  href="https://wordpress.org/plugins/insert-headers-and-footers/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Get WPCode Plugin (Free)
                </a>
              </div>

              {/* Wix */}
              <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white font-bold">Wix</div>
                  <h3 className="font-semibold text-foreground">Wix</h3>
                </div>
                <ol className="text-sm text-muted-foreground space-y-2 mb-4">
                  <li>1. Open your Wix Editor</li>
                  <li>2. Click Settings &rarr; Custom Code</li>
                  <li>3. Click "+ Add Custom Code"</li>
                  <li>4. Paste code, set to "Body - end"</li>
                  <li>5. Apply to All Pages &rarr; Save</li>
                </ol>
                <a
                  href="https://support.wix.com/en/article/embedding-custom-code-to-your-site"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-muted hover:bg-muted/80 text-foreground px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Wix Guide
                </a>
              </div>

              {/* Squarespace */}
              <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center text-white font-bold text-xs">SQ</div>
                  <h3 className="font-semibold text-foreground">Squarespace</h3>
                </div>
                <ol className="text-sm text-muted-foreground space-y-2 mb-4">
                  <li>1. Go to Settings &rarr; Advanced</li>
                  <li>2. Click "Code Injection"</li>
                  <li>3. Paste code in the Footer section</li>
                  <li>4. Click Save</li>
                </ol>
                <a
                  href="https://support.squarespace.com/hc/en-us/articles/205815908"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-muted hover:bg-muted/80 text-foreground px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Squarespace Guide
                </a>
              </div>
            </div>
          </div>

          {/* Raw Code */}
          <div className="rounded-xl border bg-card card-inset overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Installation Code</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Add this code to your website, just before the closing &lt;/body&gt; tag
                </p>
              </div>
              <button
                onClick={copyToClipboard}
                disabled={!isClientReady}
                className="bg-muted hover:bg-muted/80 text-foreground px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Code'}
              </button>
            </div>
            <div className="p-4">
              <pre className="bg-gray-900 text-gray-100 p-3 sm:p-4 rounded-lg overflow-x-auto text-[11px] sm:text-sm whitespace-pre-wrap break-all">
                <code>{isClientReady ? generateEmbedCode() : '// Loading organization details...'}</code>
              </pre>
            </div>
          </div>

          {/* Other Options */}
          <div className="rounded-xl border bg-card card-inset overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">Other Installation Methods</h2>
            </div>
            <div className="p-4 space-y-4">
              {/* Google Tag Manager */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">Google Tag Manager</h3>
                <ol className="text-sm text-muted-foreground space-y-1">
                  <li>1. Create a new Custom HTML tag</li>
                  <li>2. Paste the installation code</li>
                  <li>3. Set trigger to "All Pages - DOM Ready"</li>
                  <li>4. Save and publish</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="rounded-xl border bg-card card-inset overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Widget Configuration</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Customize the widget appearance and behavior
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Widget Key */}
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <label className="text-sm font-medium text-foreground">Widget Key</label>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <code className="flex-1 min-w-0 px-3 py-2 bg-muted border border-border rounded-lg text-xs sm:text-sm font-mono text-foreground break-all sm:truncate">
                  {config.widgetKey || config.clientId}
                </code>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const key = config.widgetKey || config.clientId
                      if (!key) return
                      navigator.clipboard.writeText(key)
                      toast({ title: 'Widget key copied' })
                    }}
                    className="flex items-center gap-1 px-3 py-2 bg-muted hover:bg-muted/80 text-foreground border border-border rounded-lg text-sm whitespace-nowrap"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                  <button
                    onClick={regenerateWidgetKey}
                    disabled={isRegeneratingKey}
                    className="flex items-center gap-1 px-3 py-2 bg-muted hover:bg-muted/80 text-foreground border border-border rounded-lg text-sm whitespace-nowrap"
                  >
                    {isRegeneratingKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Regenerate
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                This key identifies your widget. Regenerating it will invalidate existing embeds.
                All other settings (colors, theme, etc.) update automatically without changing embed code.
              </p>
            </div>

            {/* Branding */}
            <div className="border-t border-border pt-6">
              <h3 className="text-md font-semibold text-foreground mb-4">Branding</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Company Name</label>
                  <input
                    type="text"
                    value={config.companyName}
                    onChange={(e) => setConfig({ ...config, companyName: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Your Law Firm Name"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Displayed in the widget header</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Logo URL</label>
                  <input
                    type="url"
                    value={config.logoUrl}
                    onChange={(e) => setConfig({ ...config, logoUrl: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="https://yoursite.com/logo.png"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Small logo/icon shown in the widget header and proactive popup</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config.primaryColor}
                      onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                      className="h-10 w-20 rounded border border-border"
                    />
                    <input
                      type="text"
                      value={config.primaryColor}
                      onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                      className="flex-1 px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      placeholder="#C73E2D"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Used for the chat button and headers</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Theme</label>
                  <select
                    value={config.theme}
                    onChange={(e) => setConfig({ ...config, theme: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto (matches visitor's OS setting)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Voice & Animations */}
            <WidgetAnimationsSection
              webVoiceEnabled={config.webVoiceEnabled}
              onWebVoiceChange={(next) => setConfig({ ...config, webVoiceEnabled: next })}
              animations={config.widgetAnimations}
              onAnimationsChange={(next) => setConfig({ ...config, widgetAnimations: next })}
              primaryColor={config.primaryColor}
            />

            {/* Chat Behavior */}
            <div className="border-t border-border pt-6">
              <h3 className="text-md font-semibold text-foreground mb-4">Chat Behavior</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Assistant Name</label>
                  <input
                    type="text"
                    value={config.assistantName}
                    onChange={(e) => setConfig({ ...config, assistantName: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="AI Assistant"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Name displayed for the AI in chat messages</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Welcome Message</label>
                  <input
                    type="text"
                    value={config.welcomeMessage}
                    onChange={(e) => setConfig({ ...config, welcomeMessage: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Welcome! How can we help you today?"
                  />
                  <p className="text-xs text-muted-foreground mt-1">First message visitors see when they open the chat</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Conversation Starters
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Visible chips on the widget welcome screen. The <em>display label</em> is
                    what shows on the chip (keep it short — 2 to 4 words). The <em>question</em>
                    is what gets sent to the AI when a visitor taps the chip. Adding a preferred
                    answer teaches the AI to give that answer (or a close paraphrase) when a
                    visitor clicks the chip — or asks anything similar.
                  </p>
                  {(config.conversationStarters.length > 0
                    ? config.conversationStarters
                    : [{ question: '', answer: '', label: '' }]
                  ).map((starter, i) => {
                    const update = (patch: Partial<{ question: string; answer: string; label: string }>) => {
                      const next = [...config.conversationStarters]
                      if (next.length <= i) next.push({ question: '', answer: '', label: '' })
                      next[i] = { ...next[i], ...patch }
                      setConfig({ ...config, conversationStarters: next })
                    }
                    return (
                      <div
                        key={i}
                        className="mb-3 rounded-lg border border-border bg-background overflow-hidden"
                      >
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
                          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground shrink-0 w-32">
                            Display label
                          </span>
                          <input
                            type="text"
                            value={starter.label}
                            onChange={(e) => update({ label: e.target.value })}
                            maxLength={32}
                            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground border-none focus:outline-none text-sm"
                            placeholder='e.g. "Pricing" (optional — falls back to the question)'
                          />
                          {config.conversationStarters.length > 1 && (
                            <button
                              onClick={() => {
                                const updated = config.conversationStarters.filter((_, idx) => idx !== i)
                                setConfig({ ...config, conversationStarters: updated })
                              }}
                              className="text-muted-foreground hover:text-destructive text-xs px-2"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2">
                          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground shrink-0 w-32">
                            Question
                          </span>
                          <input
                            type="text"
                            value={starter.question}
                            onChange={(e) => update({ question: e.target.value })}
                            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground border-none focus:outline-none text-sm"
                            placeholder={`e.g. "How does pricing work?"`}
                          />
                        </div>
                        <div className="border-t border-border bg-muted/30 px-3 py-2">
                          <label className="block text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                            Preferred answer (optional)
                          </label>
                          <textarea
                            value={starter.answer}
                            onChange={(e) => update({ answer: e.target.value })}
                            rows={2}
                            className="w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            placeholder="Leave blank for free-form AI. Set to lock the AI's answer to this exact response (with light paraphrasing for natural flow)."
                          />
                        </div>
                      </div>
                    )
                  })}
                  {config.conversationStarters.length < 5 && (
                    <button
                      onClick={() =>
                        setConfig({
                          ...config,
                          conversationStarters: [
                            ...config.conversationStarters,
                            { question: '', answer: '', label: '' },
                          ],
                        })
                      }
                      className="text-sm text-primary hover:underline"
                    >
                      + Add starter
                    </button>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">Max 5. Empty questions are dropped on save.</p>
                </div>
              </div>
            </div>

            {/* Layout & Display */}
            <div className="border-t border-border pt-6">
              <h3 className="text-md font-semibold text-foreground mb-4">Layout & Display</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Widget Position</label>
                  <select
                    value={config.position}
                    onChange={(e) => setConfig({ ...config, position: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <option value="bottom-right">Bottom Right (Recommended)</option>
                    <option value="bottom-left">Bottom Left</option>
                  </select>
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.autoOpen}
                    onChange={(e) => setConfig({ ...config, autoOpen: e.target.checked })}
                    className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-border rounded"
                  />
                  <span className="ml-2 text-sm text-foreground">Auto-open on page load</span>
                </label>
                <p className="text-xs text-muted-foreground ml-6 -mt-2">The chat window will open automatically when visitors land on your page</p>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.showOnMobile}
                    onChange={(e) => setConfig({ ...config, showOnMobile: e.target.checked })}
                    className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-border rounded"
                  />
                  <span className="ml-2 text-sm text-foreground">Show on mobile devices</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.attentionGrabber}
                    onChange={(e) => setConfig({ ...config, attentionGrabber: e.target.checked })}
                    className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-border rounded"
                  />
                  <span className="ml-2 text-sm text-foreground">Attention grabber animation</span>
                </label>
                <p className="text-xs text-muted-foreground ml-6 -mt-2">Pulsing animation on the chat bubble to draw visitor attention</p>
              </div>
            </div>

            {/* Proactive Popup Settings */}
            <div className="border-t border-border pt-6">
              <h3 className="text-md font-semibold text-foreground mb-4">Proactive Engagement</h3>
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.proactiveEnabled}
                    onChange={(e) => setConfig({ ...config, proactiveEnabled: e.target.checked })}
                    className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-border rounded"
                  />
                  <span className="ml-2 text-sm text-foreground">Show proactive popup</span>
                </label>
                <p className="text-xs text-muted-foreground ml-6 -mt-2">A speech bubble appears above the chat icon to engage visitors before they click</p>

                {config.proactiveEnabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Delay (seconds)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="120"
                        value={config.proactiveDelay}
                        onChange={(e) => setConfig({ ...config, proactiveDelay: parseInt(e.target.value) || 15 })}
                        className="w-32 px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      />
                      <p className="text-xs text-muted-foreground mt-1">How long to wait before showing the popup</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Custom Message (optional)
                      </label>
                      <input
                        type="text"
                        value={config.proactiveMessage}
                        onChange={(e) => setConfig({ ...config, proactiveMessage: e.target.value })}
                        className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        placeholder="Hi! Have a question? I'm here to help."
                      />
                      <p className="text-xs text-muted-foreground mt-1">Leave empty for default message</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Consultation Booking */}
            <div className="border-t border-border pt-6">
              <h3 className="text-md font-semibold text-foreground mb-1">Consultation Booking</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure how visitors can request a consultation. The "Schedule a Consultation"
                button only appears in the widget if at least one option is set.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Contact Email</label>
                  <input
                    type="email"
                    value={config.contactEmail}
                    onChange={(e) => setConfig({ ...config, contactEmail: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="consultations@yourfirm.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Shows an email contact view with response time expectations. Leave blank to disable.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Online Booking URL</label>
                  <input
                    type="url"
                    value={config.bookingUrl}
                    onChange={(e) => setConfig({ ...config, bookingUrl: e.target.value })}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="https://calendly.com/yourfirm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Works with Calendly, Cal.com, Chili Piper, or any booking link. If both are set,
                    email is shown first with a direct booking link below it.
                  </p>
                </div>
              </div>
            </div>

            {/* AI Prompt — platform admin only */}
            {isPlatformAdmin && <div className="border-t border-border pt-6">
              <h3 className="text-md font-semibold text-foreground mb-1">AI Chat Prompt</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This prompt controls how your chat AI responds to visitors. You can auto-generate it from your website or write your own.
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={async () => {
                    if (!generatedPrompt) {
                      try {
                        const orgRes = await api.get('/api/organizations/current')
                        const settings = orgRes.data.organization?.settings || {}
                        setGeneratedPrompt(settings.chatSystemPrompt || '')
                      } catch {
                        setGeneratedPrompt('')
                      }
                    }
                    setShowPromptPreview(!showPromptPreview)
                  }}
                  className="flex items-center gap-2 border border-border bg-background hover:bg-muted px-4 py-2 rounded-lg text-sm"
                >
                  {showPromptPreview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showPromptPreview ? 'Hide prompt editor' : 'View & edit prompt'}
                </button>
                <button
                  onClick={() => setShowRegenConfirm(true)}
                  disabled={isRegenerating}
                  className="flex items-center gap-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/40 px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                >
                  {isRegenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Auto-generate from website
                    </>
                  )}
                </button>
              </div>

              {/* Regeneration confirmation */}
              {showRegenConfirm && (
                <div className="mb-4 p-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">Are you sure?</p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
                    This will replace your current prompt with one auto-generated from your website and knowledge base.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={regeneratePrompt}
                      className="px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium"
                    >
                      Yes, regenerate
                    </button>
                    <button
                      onClick={() => setShowRegenConfirm(false)}
                      className="px-3 py-1.5 rounded-md border border-amber-300 text-amber-800 dark:text-amber-300 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Editable prompt */}
              {showPromptPreview && (
                <div className="space-y-3">
                  <textarea
                    className="w-full bg-muted/50 border border-border rounded-lg p-4 text-xs text-foreground font-mono whitespace-pre-wrap min-h-[300px] max-h-[600px] resize-y focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    value={generatedPrompt || ''}
                    onChange={(e) => setGeneratedPrompt(e.target.value)}
                    placeholder="Paste or write your custom chat AI prompt here..."
                  />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        try {
                          setIsSaving(true)
                          await api.put('/api/organizations/widget-settings', {
                            ...config,
                            chatSystemPrompt: generatedPrompt,
                          })
                          toast({ title: 'Prompt saved', description: 'Your chat AI will use this prompt for all new conversations.' })
                        } catch {
                          toast({ title: 'Error', description: 'Failed to save prompt.', variant: 'destructive' })
                        } finally {
                          setIsSaving(false)
                        }
                      }}
                      disabled={isSaving}
                      className="bg-brand-primary hover:bg-brand-primary/90 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save Prompt
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {generatedPrompt ? `${generatedPrompt.length.toLocaleString()} characters` : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>}

            {/* Save Button */}
            <div className="pt-4">
              <button
                onClick={saveConfig}
                disabled={isSaving}
                className="bg-brand-primary hover:bg-brand-primary/90 text-white px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-semibold text-foreground mb-2">Email to Web Developer</h3>
            <p className="text-muted-foreground text-sm mb-4">
              We'll open your email app with the installation code and instructions ready to send.
            </p>

            {emailSent ? (
              <div className="text-center py-4">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-green-600 font-medium">Email app opened!</p>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Developer's Email Address
                  </label>
                  <input
                    type="email"
                    value={developerEmail}
                    onChange={(e) => setDeveloperEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="developer@example.com"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted/50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEmailToDeveloper}
                    disabled={!developerEmail}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Send Email
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
